import * as React from "react";
import { Plus } from "lucide-react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { Text } from "../../../ui/components/Text";
import { Button } from "../../../ui/components/Button";
import { Callout } from "../../../ui/components/Callout";
import { ConfirmModal } from "../../../ui/components/ConfirmModal";
import { AuthTransitionScreen } from "../../../ui/components/AuthTransitionScreen";
import { useToast } from "../../../ui/components/Toast";
import { useAuth } from "../../lib/auth/hooks";
import { useWebSocket } from "../../hooks/useWebSocket";
import { useI18n } from "../../lib/i18n";
import * as roadmapApi from "../../lib/api/roadmap";
import {
  ROADMAP_STATUSES,
  type RoadmapItem,
  type RoadmapItemPayload,
  type RoadmapStatus,
} from "../../lib/api/roadmap";
import { RoadmapSection, sectionDroppableId } from "./RoadmapSection";
import { RoadmapCardView } from "./RoadmapCard";
import { RoadmapItemModal } from "./RoadmapItemModal";
import * as s from "./RoadmapPage.css";

const CHANNEL = "entity:roadmap:global";

type Columns = Record<RoadmapStatus, string[]>;

const ICON_CLASS: Record<RoadmapStatus, string> = {
  done: s.iconDone,
  in_progress: s.iconInProgress,
  planned: s.iconPlanned,
  known_bug: s.iconKnownBug,
};

function buildColumns(items: RoadmapItem[]): Columns {
  const columns: Columns = { done: [], in_progress: [], planned: [], known_bug: [] };
  [...items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .forEach((item) => columns[item.status]?.push(item.id));
  return columns;
}

function findStatus(columns: Columns, id: UniqueIdentifier | undefined): RoadmapStatus | null {
  if (id === undefined) return null;
  const key = String(id);
  const section = ROADMAP_STATUSES.find((status) => sectionDroppableId(status) === key);
  if (section) return section;
  return ROADMAP_STATUSES.find((status) => columns[status].includes(key)) ?? null;
}

export function RoadmapPage() {
  const { accessToken } = useAuth();
  const { t } = useI18n();
  const toast = useToast();
  const { subscribe, unsubscribe } = useWebSocket();
  const token = accessToken ?? (typeof localStorage !== "undefined" ? localStorage.getItem("access_token") : null);

  const [itemsById, setItemsById] = React.useState<Record<string, RoadmapItem>>({});
  const [columns, setColumns] = React.useState<Columns | null>(null);
  const [loadError, setLoadError] = React.useState<string | null>(null);
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [modal, setModal] = React.useState<{ item: RoadmapItem | null } | null>(null);
  const [pendingDelete, setPendingDelete] = React.useState<RoadmapItem | null>(null);
  const [deleting, setDeleting] = React.useState(false);
  const dragStart = React.useRef<{ columns: Columns; status: RoadmapStatus; index: number } | null>(null);

  const load = React.useCallback(async () => {
    if (!token) return;
    try {
      const items = await roadmapApi.fetchRoadmapItems(token);
      setItemsById(Object.fromEntries(items.map((i) => [i.id, i])));
      setColumns(buildColumns(items));
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t("pro.system.roadmap.error.load"));
    }
  }, [token, t]);

  React.useEffect(() => {
    void load();
  }, [load]);

  React.useEffect(() => {
    const handler = () => {
      if (!dragStart.current) void load();
    };
    subscribe([CHANNEL], handler);
    return () => unsubscribe([CHANNEL], handler);
  }, [subscribe, unsubscribe, load]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = ({ active }: DragStartEvent) => {
    if (!columns) return;
    const status = findStatus(columns, active.id);
    if (!status) return;
    dragStart.current = {
      columns,
      status,
      index: columns[status].indexOf(String(active.id)),
    };
    setActiveId(String(active.id));
  };

  const handleDragOver = ({ active, over }: DragOverEvent) => {
    setColumns((prev) => {
      if (!prev || !over) return prev;
      const from = findStatus(prev, active.id);
      const to = findStatus(prev, over.id);
      if (!from || !to || from === to) return prev;
      const id = String(active.id);
      const target = prev[to];
      const overIndex = target.indexOf(String(over.id));
      const insertAt = overIndex >= 0 ? overIndex : target.length;
      return {
        ...prev,
        [from]: prev[from].filter((x) => x !== id),
        [to]: [...target.slice(0, insertAt), id, ...target.slice(insertAt)],
      };
    });
  };

  const resetDrag = () => {
    dragStart.current = null;
    setActiveId(null);
  };

  const handleDragCancel = () => {
    if (dragStart.current) setColumns(dragStart.current.columns);
    resetDrag();
  };

  const handleDragEnd = async ({ active, over }: DragEndEvent) => {
    const start = dragStart.current;
    if (!start || !columns || !over || !token) {
      handleDragCancel();
      return;
    }
    const id = String(active.id);
    const status = findStatus(columns, active.id);
    const overStatus = findStatus(columns, over.id);
    if (!status || status !== overStatus) {
      handleDragCancel();
      return;
    }

    let next = columns;
    const oldIndex = columns[status].indexOf(id);
    const overIndex = columns[status].indexOf(String(over.id));
    if (overIndex >= 0 && overIndex !== oldIndex) {
      next = { ...columns, [status]: arrayMove(columns[status], oldIndex, overIndex) };
      setColumns(next);
    }
    const index = next[status].indexOf(id);
    resetDrag();
    if (status === start.status && index === start.index) return;

    setItemsById((prev) => ({ ...prev, [id]: { ...prev[id], status } }));
    try {
      const moved = await roadmapApi.moveRoadmapItem(token, id, status, index);
      setItemsById((prev) => ({ ...prev, [id]: moved }));
    } catch (err) {
      setColumns(start.columns);
      setItemsById((prev) => ({ ...prev, [id]: { ...prev[id], status: start.status } }));
      toast.error(t("pro.system.roadmap.error.move"), err instanceof Error ? err.message : undefined);
    }
  };

  const handleSubmit = async (payload: RoadmapItemPayload) => {
    if (!token || !modal) return;
    const existing = modal.item;
    if (existing) {
      const { status, ...fields } = payload;
      await roadmapApi.updateRoadmapItem(token, existing.id, fields);
      if (status !== existing.status) {
        await roadmapApi.moveRoadmapItem(token, existing.id, status, columns?.[status].length ?? 0);
      }
    } else {
      await roadmapApi.createRoadmapItem(token, payload);
    }
    setModal(null);
    await load();
  };

  const handleDelete = async () => {
    if (!token || !pendingDelete) return;
    setDeleting(true);
    try {
      await roadmapApi.deleteRoadmapItem(token, pendingDelete.id);
      setPendingDelete(null);
      await load();
    } catch (err) {
      toast.error(t("pro.system.roadmap.error.delete"), err instanceof Error ? err.message : undefined);
    } finally {
      setDeleting(false);
    }
  };

  if (!token) {
    return (
      <div className={s.page}>
        <Callout variant="error">{t("pro.system.roadmap.signInRequired")}</Callout>
      </div>
    );
  }

  const activeItem = activeId ? itemsById[activeId] : null;

  return (
    <div className={s.page}>
      <div className={s.pageHeader}>
        <Text level="h1" color="text" weight="bold">
          {t("pro.system.roadmap.title")}
        </Text>
        <Button color="primary" iconLeft={<Plus size={14} />} onClick={() => setModal({ item: null })}>
          {t("pro.system.roadmap.add")}
        </Button>
      </div>

      {loadError && (
        <div className={s.errorWrap}>
          <Callout variant="error">{loadError}</Callout>
        </div>
      )}

      {!columns && !loadError ? (
        <AuthTransitionScreen variant="fill" />
      ) : columns ? (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className={s.board}>
            {ROADMAP_STATUSES.map((status) => (
              <RoadmapSection
                key={status}
                status={status}
                iconClassName={ICON_CLASS[status]}
                items={columns[status].map((id) => itemsById[id]).filter(Boolean)}
                onEdit={(item) => setModal({ item })}
                onDelete={setPendingDelete}
              />
            ))}
          </div>
          <DragOverlay>{activeItem ? <RoadmapCardView item={activeItem} overlay /> : null}</DragOverlay>
        </DndContext>
      ) : null}

      <RoadmapItemModal
        open={modal !== null}
        item={modal?.item ?? null}
        onClose={() => setModal(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={pendingDelete !== null}
        title={t("pro.system.roadmap.delete.title")}
        body={t("pro.system.roadmap.delete.body", { name: pendingDelete?.name ?? "" })}
        confirmLabel={t("pro.system.roadmap.delete.confirm")}
        confirmColor="danger"
        loading={deleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleDelete}
      />
    </div>
  );
}
